/** CHAT & MESSAGES — the super admin's 7-day record, inside Audit Logs.
 *
 *  Read-only. Every public chat message and every manager announcement is
 *  here for seven days, with the screen name, the establishment (or "Off
 *  loc"), the time and whether it was reported or deleted. A message deleted
 *  from the players' screens is STILL here. There is no email address and no
 *  phone number in this record, because none is ever written.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Search } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

export const ChatAuditPanel = ({ organizationId = 'all' }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [player, setPlayer] = useState('');
    const [day, setDay] = useState('');
    const [total, setTotal] = useState(0);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/admin/chat-audit', {
                params: {
                    organization_id: organizationId || 'all',
                    player: player || undefined,
                    start_date: day || undefined,
                    end_date: day || undefined,
                },
            });
            setRows(res.data?.rows || []);
            setTotal(res.data?.total || 0);
        } catch (_e) { setRows([]); setTotal(0); }
    }, [organizationId, player, day]);

    useEffect(() => {
        const id = setTimeout(load, 250);
        return () => clearTimeout(id);
    }, [load]);

    return (
        <Card data-testid="chat-audit-panel">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-sky-500" />
                    {t('auditLogs.chat.chatTab', 'Chat & messages')}
                    <Badge variant="secondary" className="ml-1" data-testid="chat-audit-count">{total}</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground" data-testid="chat-audit-intro">
                    {t('auditLogs.chat.chatIntro', 'Player chat, kept for 7 days and then deleted automatically. Read-only.')}
                </p>
                <div className="flex flex-wrap items-end gap-3">
                    <div className="relative min-w-[200px] flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input value={player} onChange={(e) => setPlayer(e.target.value)}
                               placeholder={t('auditLogs.chat.chatFilterPlayer', 'Filter by player')}
                               className="pl-9" data-testid="chat-audit-player" />
                    </div>
                    <Input type="date" value={day} onChange={(e) => setDay(e.target.value)}
                           className="w-[170px]" data-testid="chat-audit-date" />
                </div>

                {rows.length === 0 ? (
                    <p className="py-4 text-center text-xs text-muted-foreground" data-testid="chat-audit-empty">
                        {t('auditLogs.chat.chatEmpty', 'No chat messages in this range.')}
                    </p>
                ) : (
                    <div className="space-y-2" data-testid="chat-audit-rows">
                        {rows.map((r) => (
                            <div key={r.id} className="rounded-lg border border-border p-2.5 text-sm"
                                 data-testid="chat-audit-row">
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <span className="font-semibold" data-testid="chat-audit-name">{r.sender_name}</span>
                                    <span className="text-[11px] text-muted-foreground tabular-nums"
                                          data-testid="chat-audit-when">
                                        {new Date(r.created_at).toLocaleString()}
                                    </span>
                                </div>
                                <p className="text-[11px] text-muted-foreground" data-testid="chat-audit-place">
                                    {r.establishment || t('goalGame.chatOffLoc', 'Off loc')}
                                </p>
                                <p className="mt-1 break-words" data-testid="chat-audit-text">{r.text}</p>
                                <div className="mt-1.5 flex flex-wrap gap-2">
                                    {r.kind === 'announcement' && (
                                        <Badge variant="outline" className="text-[10px]">announcement</Badge>
                                    )}
                                    {r.reported && (
                                        <Badge variant="outline" className="text-[10px] text-amber-600"
                                               data-testid="chat-audit-reported">
                                            {t('goalGame.chatReported', 'Reported')}
                                        </Badge>
                                    )}
                                    {r.deleted && (
                                        <Badge variant="outline" className="text-[10px] text-red-600"
                                               data-testid="chat-audit-deleted">
                                            {t('liveGame.chatDeleted', 'Deleted')}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default ChatAuditPanel;
